import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDisclosure } from "@mantine/hooks";
import { useCellRender } from "../../../hooks/useCellRender";
import { useFormatter } from "../../../hooks/useFomatter";
import { useServerApi } from "../../../hooks/userServerApi";
import { openFileUrl } from "../../../utils/openReport";
import {
  IconWallet,
  IconList,
  IconReceipt,
  IconPlayerPlay,
  IconX,
  IconArrowBack,
  IconCircleCheck,
  IconTransferOut,
  IconDownload,
} from "@tabler/icons";
import {
  Button,
  Text,
  Title,
  Badge,
  Tabs,
  Accordion,
  LoadingOverlay,
  Box,
  Paper,
  Table,
  Grid,
  Group,
  Stack,
  Avatar,
  ActionIcon,
  Tooltip,
  Checkbox,
  Modal,
  Alert,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import HoldingByOrderFilterBar from "./HoldingByOrderFilterBar";
import HoldingByOrderRowHeader from "./HoldingByOrderRowHeader";
import EntityTabAnchor from "./EntityTabAnchor";
import ReleasedFromPoolTabContent from "./ReleasedFromPoolTabContent";
import { formatPayrollBidDate, formatPayrollBidHeldDate } from "./payrollSourceHelpers";
import {
  applyHoldingByOrderFilters,
  collectHoldingBidIds,
  getHoldingBulkCounts,
} from "./holdingByOrderFilterUtils";

const HoldingBidsTab = ({ form }) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [cellRender] = useCellRender();

  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [summary, setSummary] = useState([]);
  const [holdCount, setHoldCount] = useState(0);
  const [totalHoldAmount, setTotalHoldAmount] = useState(0);
  const [cancelCount, setCancelCount] = useState(0);
  const [totalCancelAmount, setTotalCancelAmount] = useState(0);
  const [innerTab, setInnerTab] = useState("byWallets");
  const [expandedWallet, setExpandedWallet] = useState(null);
  const [walletBidsMap, setWalletBidsMap] = useState({});
  const [loadingWalletId, setLoadingWalletId] = useState(null);
  const [allBids, setAllBids] = useState([]);
  const [allBidsPage, setAllBidsPage] = useState(1);
  const [allBidsTotal, setAllBidsTotal] = useState(0);
  const [allBidsLimit] = useState(100);
  const [loadingAllBids, setLoadingAllBids] = useState(false);
  const [selectedBids, setSelectedBids] = useState(new Set());
  // By Orders - lazy load
  const [orderGroups, setOrderGroups] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderBidsMap, setOrderBidsMap] = useState({});
  const [loadingOrderKey, setLoadingOrderKey] = useState(null);
  const [allHoldingBids, setAllHoldingBids] = useState([]);
  const [orderTableFilterQuery, setOrderTableFilterQuery] = useState({});
  const [pendingBulkOrderAction, setPendingBulkOrderAction] = useState(null);
  const [releasedBids, setReleasedBids] = useState([]);
  const [releasedCount, setReleasedCount] = useState(0);
  const [releasedTotalAmount, setReleasedTotalAmount] = useState(0);
  const releasedBidsCacheValid = useRef(false);
  const [releasedFromBids, setReleasedFromBids] = useState([]);
  const [releasedFromCount, setReleasedFromCount] = useState(0);
  const [releasedFromTotalAmount, setReleasedFromTotalAmount] = useState(0);
  const [releasedFromLoading, setReleasedFromLoading] = useState(false);
  const releasedFromBidsCacheValid = useRef(false);

  const payrollStatus = form?.values?.status;
  const isConfirmed =
    payrollStatus === "CONFIRMED" || payrollStatus === "COMPLETED";
  const payrollId = form?.values?._id;

  const [
    holdBackAllModalOpened,
    { open: openHoldBackAllModal, close: closeHoldBackAllModal },
  ] = useDisclosure(false);
  const [
    bulkOrderModalOpened,
    { open: openBulkOrderModal, close: closeBulkOrderModal },
  ] = useDisclosure(false);

  const filteredOrderGroups = useMemo(
    () => applyHoldingByOrderFilters(orderGroups, orderTableFilterQuery),
    [orderGroups, orderTableFilterQuery],
  );

  const holdingBulkCounts = useMemo(
    () => getHoldingBulkCounts(allHoldingBids, filteredOrderGroups),
    [allHoldingBids, filteredOrderGroups],
  );

  const holdBackEligibleBids = useMemo(
    () => releasedBids.filter((b) => ["PENDING", "HOLD"].includes(b.status)),
    [releasedBids],
  );
  const holdBackEligibleCount = holdBackEligibleBids.length;
  const holdBackEligibleAmount = useMemo(
    () =>
      holdBackEligibleBids.reduce(
        (sum, b) => sum + (b.commissionAmount || 0),
        0,
      ),
    [holdBackEligibleBids],
  );
  const hasStuckHoldReleasedBids = useMemo(
    () => releasedBids.some((b) => b.status === "HOLD"),
    [releasedBids],
  );

  const loadSummary = async (silent = false) => {
    if (!payrollId) return;
    try {
      if (!silent) setLoading(true);
      const result = await api.Payroll2.getHoldingBids(payrollId, {
        groupBy: "wallet",
      });
      if (result?.success && result.data) {
        setSummary(result.data.summary || []);
        setHoldCount(result.data.holdCount || 0);
        setTotalHoldAmount(result.data.totalHoldAmount || 0);
        setCancelCount(result.data.cancelCount || 0);
        setTotalCancelAmount(result.data.totalCancelAmount || 0);
        setReleasedCount(result.data.releasedCount || 0);
        form.setFieldValue(
          "holdingBidsCount",
          (result.data.holdCount || 0) + (result.data.cancelCount || 0),
        );
      }
    } catch (error) {
      console.log("loadSummary error:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadAllBids = async (page = 1) => {
    if (!payrollId) return;
    try {
      setLoadingAllBids(true);
      setSelectedBids(new Set());
      const result = await api.Payroll2.getHoldingBids(payrollId, {
        groupBy: "all",
        page,
        limit: allBidsLimit,
      });
      if (result?.success && result.data) {
        setAllBids(result.data.bids || []);
        setAllBidsTotal(result.data.totalBids ?? 0);
        setAllBidsPage(page);
      }
    } catch (error) {
      console.log("loadAllBids error:", error);
    } finally {
      setLoadingAllBids(false);
    }
  };

  const loadAllHoldingBidsCached = async () => {
    const pageSize = 500;
    let page = 1;
    const all = [];
    const seenIds = new Set();
    let total = 0;
    do {
      const result = await api.Payroll2.getHoldingBids(payrollId, {
        groupBy: "all",
        page,
        limit: pageSize,
      });
      if (!result?.success) break;
      const bids = result.data.bids || [];
      total = result.data.totalBids ?? bids.length;
      for (const bid of bids) {
        const id = bid?._id?.toString?.() ?? bid?._id;
        if (!id || seenIds.has(id)) continue;
        seenIds.add(id);
        all.push(bid);
      }
      page += 1;
    } while (all.length < total);
    return all;
  };

  const loadAllHoldingBidsForBulk = async (force = false) => {
    if (!payrollId) return;
    if (!force && allHoldingBids.length > 0) return;
    try {
      const bids = await loadAllHoldingBidsCached();
      setAllHoldingBids(bids);
    } catch (error) {
      console.log("loadAllHoldingBidsForBulk error:", error);
    }
  };

  const loadByOrdersData = async (force = false, silent = false) => {
    if (!payrollId) return;
    if (!force && orderGroups.length > 0) return;
    try {
      if (!silent) setLoading(true);
      const groupsResult = await api.Payroll2.getHoldingBids(payrollId, {
        groupBy: "order",
      });
      if (groupsResult?.success && groupsResult.data) {
        setOrderGroups(groupsResult.data.groups || []);
      }
    } catch (error) {
      console.log("loadByOrdersData error:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadReleasedBids = async (silent = false, force = false) => {
    if (!payrollId) return;
    if (!force && releasedBidsCacheValid.current) return;
    try {
      if (!silent) setLoading(true);
      const result = await api.Payroll2.getReleasedHoldingBids(payrollId);
      if (result?.success && result.data) {
        setReleasedBids(result.data.bids || []);
        setReleasedCount(result.data.totalBids ?? 0);
        setReleasedTotalAmount(result.data.totalAmount || 0);
        releasedBidsCacheValid.current = true;
      }
    } catch (error) {
      console.log("loadReleasedBids error:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const invalidateReleasedBidsCache = () => {
    releasedBidsCacheValid.current = false;
    releasedFromBidsCacheValid.current = false;
  };

  const loadReleasedFromBids = async (silent = false, force = false) => {
    if (!payrollId) return;
    if (!force && releasedFromBidsCacheValid.current) return;
    try {
      if (!silent) setReleasedFromLoading(true);
      const result = await api.Payroll2.getReleasedFromPayrollBids(payrollId);
      if (result?.success && result.data) {
        setReleasedFromBids(result.data.bids || []);
        setReleasedFromCount(result.data.totalBids ?? 0);
        setReleasedFromTotalAmount(result.data.totalAmount || 0);
        releasedFromBidsCacheValid.current = true;
      }
    } catch (error) {
      console.log("loadReleasedFromBids error:", error);
    } finally {
      if (!silent) setReleasedFromLoading(false);
    }
  };

  const walletAccordionValue = useMemo(() => {
    if (!expandedWallet) return null;
    const walletKey = String(expandedWallet);
    const exists = summary.some(
      (item) => item.walletId && String(item.walletId) === walletKey,
    );
    return exists ? walletKey : null;
  }, [expandedWallet, summary]);

  const orderAccordionValue = useMemo(() => {
    if (!expandedOrder) return null;
    const orderKey = String(expandedOrder);
    const exists = orderGroups.some(
      (order) => String(order._id || order.key) === orderKey,
    );
    return exists ? orderKey : null;
  }, [expandedOrder, orderGroups]);

  const loadWalletBids = async (walletId) => {
    if (!payrollId || !walletId || walletBidsMap[walletId]) return;
    try {
      setLoadingWalletId(walletId);
      const result = await api.Payroll2.getHoldingBidsByWallet(
        payrollId,
        walletId,
      );
      if (result?.success && result.data?.bids) {
        setWalletBidsMap((prev) => ({
          ...prev,
          [walletId]: result.data.bids,
        }));
      }
    } catch (error) {
      console.log("loadWalletBids error:", error);
    } finally {
      setLoadingWalletId(null);
    }
  };

  const reload = async () => {
    setWalletBidsMap({});
    setOrderBidsMap({});
    setSelectedBids(new Set());
    invalidateReleasedBidsCache();
    await loadSummary(true);
    await loadReleasedBids(true, true);
    await loadReleasedFromBids(true, true);
    setOrderGroups([]);
    await loadByOrdersData(true, true);
    if (innerTab === "allBids") {
      await loadAllBids(allBidsPage);
    }
    if (innerTab === "byOrders") {
      setAllHoldingBids([]);
      setOrderBidsMap({});
      setOrderTableFilterQuery({});
      await loadAllHoldingBidsForBulk(true);
      if (expandedOrder) {
        await loadOrderBids(expandedOrder, true);
      }
    }
    if (expandedWallet) {
      await loadWalletBids(expandedWallet);
    }
  };

  useEffect(() => {
    invalidateReleasedBidsCache();
    setOrderGroups([]);
    setOrderBidsMap({});
    setAllHoldingBids([]);
    setExpandedOrder(null);
    loadSummary();
    loadByOrdersData(false, true);
    loadReleasedFromBids(true);
  }, [payrollId]);

  const loadOrderBids = async (groupKey, force = false) => {
    if (!payrollId || !groupKey) return;
    if (!force && orderBidsMap[groupKey] !== undefined) return;
    try {
      setLoadingOrderKey(groupKey);
      const result = await api.Payroll2.getHoldingBidsByOrder(
        payrollId,
        groupKey,
      );
      if (result?.success && result.data?.bids) {
        setOrderBidsMap((prev) => ({
          ...prev,
          [groupKey]: result.data.bids,
        }));
      }
    } catch (error) {
      console.log("loadOrderBids error:", error);
    } finally {
      setLoadingOrderKey(null);
    }
  };

  const handleInnerTabChange = (tab) => {
    setInnerTab(tab);
    setSelectedBids(new Set());
    if (tab !== "byWallets") {
      setExpandedWallet(null);
    }
    if (tab !== "byOrders") {
      setExpandedOrder(null);
    }
    if (tab === "allBids") {
      loadAllBids(1);
    }
    if (tab === "byOrders") {
      if (orderGroups.length === 0) {
        loadByOrdersData();
      }
      loadAllHoldingBidsForBulk();
    }
    if (tab === "releasedToPayroll") {
      loadReleasedBids();
    }
    if (tab === "releasedFromPayroll") {
      loadReleasedFromBids();
    }
  };

  const handleWalletAccordionChange = (value) => {
    setExpandedWallet(value);
    if (value) loadWalletBids(value);
  };

  const handleOrderAccordionChange = (key) => {
    setExpandedOrder(key);
    if (key) loadOrderBids(key);
  };

  const onOrderTableFilterChange = useCallback(
    (field, values, possibleValues) => {
      if (!field) return;
      const newFilterQuery = { ...orderTableFilterQuery };

      if (values.length === 0) {
        newFilterQuery[field] = null;
        setOrderTableFilterQuery(newFilterQuery);
        return;
      }

      if (
        values &&
        possibleValues &&
        values.length === possibleValues.length &&
        newFilterQuery[field]
      ) {
        delete newFilterQuery[field];
        setOrderTableFilterQuery(newFilterQuery);
        return;
      }

      newFilterQuery[field] = { $in: values };
      setOrderTableFilterQuery(newFilterQuery);
    },
    [orderTableFilterQuery],
  );

  const executeBulkOrderAction = async (action) => {
    const ids = collectHoldingBidIds(
      filteredOrderGroups,
      allHoldingBids,
      action,
    );
    if (!ids.length) return;
    closeBulkOrderModal();
    setPendingBulkOrderAction(null);
    if (action === "release") await handleRelease(ids);
    else if (action === "cancel") await handleCancel(ids);
    else if (action === "revert") await handleRevert(ids);
  };

  const handleBulkOrderClick = (action) => {
    const count = holdingBulkCounts[action];
    if (!count || isConfirmed) return;
    if (count > 10) {
      setPendingBulkOrderAction(action);
      openBulkOrderModal();
      return;
    }
    executeBulkOrderAction(action);
  };

  const handleRelease = async (bidIds) => {
    if (!payrollId || !bidIds || bidIds.length === 0) return;
    try {
      setLoading(true);
      const result = await api.Payroll2.releaseHoldingBids(payrollId, bidIds);
      if (result?.success) {
        await reload();
      }
    } catch (error) {
      console.log("release error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleHoldBack = async (bidIds) => {
    if (!payrollId || !bidIds || bidIds.length === 0) return;
    try {
      setLoading(true);
      const result = await api.Payroll2.holdBackHoldingBids(payrollId, bidIds);
      if (result?.success) {
        const modifiedCount = result.data?.modifiedCount ?? bidIds.length;
        if (modifiedCount < bidIds.length) {
          showNotification({
            title: "Hold back partially completed",
            message: `${modifiedCount} of ${bidIds.length} bids were returned to the pool.`,
            color: "orange",
          });
        }
        await reload();
      }
    } catch (error) {
      console.log("holdBack error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleHoldBackAllConfirm = async () => {
    const ids = holdBackEligibleBids.map((b) => b._id);
    closeHoldBackAllModal();
    await handleHoldBack(ids);
  };

  const handleCancel = async (bidIds) => {
    if (!payrollId || !bidIds || bidIds.length === 0) return;
    try {
      setLoading(true);
      await api.Payroll2.cancelHoldingBids(payrollId, bidIds);
      await reload();
    } catch (error) {
      console.log("cancel error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = async (bidIds) => {
    if (!bidIds || bidIds.length === 0) return;
    try {
      setLoading(true);
      await api.Payroll2.revertCancelledBids(bidIds);
      await reload();
    } catch (error) {
      console.log("revert error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseWallet = async (walletId) => {
    const bids = walletBidsMap[walletId] || [];
    const holdBidIds = bids
      .filter((b) => b.status === "HOLD")
      .map((b) => b._id);
    if (holdBidIds.length > 0) await handleRelease(holdBidIds);
  };

  const handleCancelWallet = async (walletId) => {
    const bids = walletBidsMap[walletId] || [];
    const holdBidIds = bids
      .filter((b) => b.status === "HOLD")
      .map((b) => b._id);
    if (holdBidIds.length > 0) await handleCancel(holdBidIds);
  };

  const handleBulkRelease = async () => {
    const ids = Array.from(selectedBids);
    if (ids.length > 0) await handleRelease(ids);
  };

  const handleBulkCancel = async () => {
    const ids = Array.from(selectedBids);
    if (ids.length > 0) await handleCancel(ids);
  };

  const handleBulkRevert = async () => {
    const ids = Array.from(selectedBids);
    if (ids.length > 0) await handleRevert(ids);
  };

  const toggleBidSelection = (bidId) => {
    setSelectedBids((prev) => {
      const next = new Set(prev);
      if (next.has(bidId)) {
        next.delete(bidId);
      } else {
        next.add(bidId);
      }
      return next;
    });
  };

  const toggleSelectAll = (bids) => {
    const allIds = bids.map((b) => b._id);
    const allSelected = allIds.every((id) => selectedBids.has(id));
    setSelectedBids((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const totalBids = holdCount + cancelCount;
  const canExportHoldingReport =
    totalBids + releasedCount + releasedFromCount > 0;

  const handleDownloadHoldingReport = async () => {
    if (!payrollId || !canExportHoldingReport) return;
    try {
      setDownloadLoading(true);
      const result = await api.Payroll2.getHoldingBidsReport(payrollId);
      if (result?.success && result?.data?.url) {
        await openFileUrl(result.data.url, { mode: "download" });
      } else {
        showNotification({
          title: "Download failed",
          message: result?.message || "Unable to generate holding bids report.",
          color: "red",
        });
      }
    } catch (error) {
      console.log("handleDownloadHoldingReport error:", error);
      showNotification({
        title: "Download failed",
        message: error?.message || "Unable to download holding bids report.",
        color: "red",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "HOLD":
        return "orange";
      case "CANCEL":
        return "red";
      case "PENDING":
        return "blue";
      case "SETTLED":
        return "green";
      default:
        return "gray";
    }
  };

  const getSourceLabel = (bid) => {
    if (bid.sourceType === "ADJUSTMENT") return "Adjustment";
    if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote)
      return "Client Payment";
    if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order)
      return "Comm. Statement";
    if (bid.sourceType === "INCENTIVE" || bid.incentive)
      return "Incentive/Charge";
    return bid.sourceType;
  };

  const getSourceColor = (bid) => {
    if (bid.sourceType === "ADJUSTMENT") return "red";
    if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote) return "blue";
    if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order) return "violet";
    if (bid.sourceType === "INCENTIVE" || bid.incentive) return "teal";
    return "gray";
  };

  const getBidWalletDisplay = (bid) => {
    const walletUser = bid.wallet?.user;
    const walletUserGroup = bid.wallet?.userGroup;
    const isGroupBidWallet =
      bid.wallet?.type === "GROUP" || !!walletUserGroup;
    const walletName = isGroupBidWallet
      ? walletUserGroup?.name ||
        bid.wallet?.name ||
        bid.commissionRow?.userGroup?.name ||
        "-"
      : walletUser?.name || bid.wallet?.name || "-";
    const staffId = isGroupBidWallet ? null : walletUser?.staffId;
    return { walletName, staffId };
  };

  const renderBidWalletCell = (bid) => {
    const { walletName, staffId } = getBidWalletDisplay(bid);
    if (!bid.wallet && walletName === "-") {
      return (
        <Text size="xs" color="dimmed">
          -
        </Text>
      );
    }
    return (
      <Stack spacing={0}>
        {staffId && (
          <Text size="xs" color="dimmed">
            {staffId}
          </Text>
        )}
        <Text size="xs" weight={500} lineClamp={1}>
          {walletName}
        </Text>
      </Stack>
    );
  };

  const renderReleasedBidTable = (bids) => (
    <Table fontSize="xs" striped highlightOnHover withBorder mt="md">
      <thead>
        <tr>
          <th style={{ width: 40, textAlign: "center" }}>#</th>
          <th style={{ minWidth: 100 }}>Status</th>
          <th style={{ minWidth: 150 }}>Bid</th>
          <th style={{ minWidth: 120 }}>Wallet</th>
          <th style={{ minWidth: 100 }}>Source</th>
          <th style={{ minWidth: 100 }}>Order</th>
          <th style={{ minWidth: 100 }}>Debit Note</th>
          <th style={{ minWidth: 100 }}>Original Payroll</th>
          <th style={{ minWidth: 100 }}>Held Date</th>
          <th style={{ minWidth: 100 }}>Released Date</th>
          <th style={{ width: 120, textAlign: "right" }}>Amount</th>
          {!isConfirmed && (
            <th style={{ width: 70, textAlign: "center" }}>Actions</th>
          )}
        </tr>
      </thead>
      <tbody>
        {bids.map((bid, index) => (
          <tr key={bid._id || index}>
            <td style={{ textAlign: "center" }}>
              <Text size="xs" color="dimmed">
                {index + 1}
              </Text>
            </td>
            <td>
              <Badge
                size="xs"
                color={getStatusColor(bid.status)}
                variant="filled"
              >
                {bid.status}
              </Badge>
            </td>
            <td>
              <Group noWrap spacing={1}>
                <Badge
                  size="xs"
                  color={getSourceColor(bid)}
                  variant="light"
                  radius="0"
                >
                  {getSourceLabel(bid)}
                </Badge>
              </Group>
              <Text size="xs" weight={500}>
                {bid.code}
              </Text>
            </td>
            <td>{renderBidWalletCell(bid)}</td>
            <td>
              <Text size="xs" color="dimmed">
                {getSourceLabel(bid)}
              </Text>
            </td>
            <td>
              <EntityTabAnchor
                entity="order"
                id={bid.order?._id || bid.order}
                label={bid.order?.code}
              />
            </td>
            <td>
              <EntityTabAnchor
                entity="debitNote"
                id={bid.debitNote?._id || bid.debitNote}
                label={bid.debitNote?.code}
              />
            </td>
            <td>
              {bid.originalPayroll ? (
                <Text size="xs" color="dimmed">
                  {bid.originalPayroll.code || "-"}
                </Text>
              ) : (
                <Text size="xs" color="dimmed">
                  -
                </Text>
              )}
            </td>
            <td>
              <Text size="xs" color="dimmed">
                {formatPayrollBidHeldDate(bid)}
              </Text>
            </td>
            <td>
              <Text size="xs" color="dimmed">
                {formatPayrollBidDate(bid.releasedFromPoolAt)}
              </Text>
            </td>
            <td style={{ textAlign: "right" }}>
              <Text
                size="xs"
                weight={600}
                color={bid.commissionAmount >= 0 ? "teal" : "red"}
              >
                {formatter.currency(bid.commissionAmount)}
              </Text>
            </td>
            {!isConfirmed && (
              <td style={{ textAlign: "center" }}>
                {["PENDING", "HOLD"].includes(bid.status) && (
                  <Tooltip label="Hold back to pool">
                    <ActionIcon
                      size="sm"
                      color="orange"
                      variant="filled"
                      onClick={() => handleHoldBack([bid._id])}
                      disabled={loading}
                    >
                      <IconArrowBack size={12} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const renderBidTable = (bids, showSelect = false) => (
    <Table fontSize="xs" striped highlightOnHover withBorder mt="md">
      <thead>
        <tr>
          {showSelect && !isConfirmed && (
            <th style={{ width: 40, textAlign: "center" }}>
              <Checkbox
                size="xs"
                checked={
                  bids.length > 0 && bids.every((b) => selectedBids.has(b._id))
                }
                indeterminate={
                  bids.some((b) => selectedBids.has(b._id)) &&
                  !bids.every((b) => selectedBids.has(b._id))
                }
                onChange={() => toggleSelectAll(bids)}
              />
            </th>
          )}
          <th style={{ width: 40, textAlign: "center" }}>#</th>
          <th style={{ minWidth: 100 }}>Status</th>
          <th style={{ minWidth: 150 }}>Bid</th>
          <th style={{ minWidth: 120 }}>Wallet</th>
          <th style={{ minWidth: 100 }}>Source</th>
          <th style={{ minWidth: 100 }}>Order</th>
          <th style={{ minWidth: 100 }}>Debit Note</th>
          <th style={{ minWidth: 100 }}>Payroll</th>
          <th style={{ minWidth: 100 }}>Held Date</th>
          <th style={{ width: 120, textAlign: "right" }}>Amount</th>
          {!isConfirmed && (
            <th style={{ width: 140, textAlign: "center" }}>Actions</th>
          )}
        </tr>
      </thead>
      <tbody>
        {bids.map((bid, index) => (
          <tr key={bid._id || index}>
            {showSelect && !isConfirmed && (
              <td style={{ textAlign: "center" }}>
                <Checkbox
                  size="xs"
                  checked={selectedBids.has(bid._id)}
                  onChange={() => toggleBidSelection(bid._id)}
                />
              </td>
            )}
            <td style={{ textAlign: "center" }}>
              <Text size="xs" color="dimmed">
                {index + 1}
              </Text>
            </td>
            <td>
              <Badge
                size="xs"
                color={getStatusColor(bid.status)}
                variant="filled"
              >
                {bid.status}
              </Badge>
            </td>
            <td>
              <Group noWrap spacing={1}>
                <Badge
                  size="xs"
                  color={getSourceColor(bid)}
                  variant="light"
                  radius="0"
                >
                  {getSourceLabel(bid)}
                </Badge>
              </Group>
              <Text size="xs" weight={500}>
                {bid.code}
              </Text>
            </td>
            <td>{renderBidWalletCell(bid)}</td>
            <td>
              <Text size="xs" color="dimmed">
                {getSourceLabel(bid)}
              </Text>
            </td>
            <td>
              <EntityTabAnchor
                entity="order"
                id={bid.order?._id || bid.order}
                label={bid.order?.code}
              />
            </td>
            <td>
              <EntityTabAnchor
                entity="debitNote"
                id={bid.debitNote?._id || bid.debitNote}
                label={bid.debitNote?.code}
              />
            </td>
            <td>
              <EntityTabAnchor
                entity="payroll"
                id={bid.payroll?._id || bid.payroll}
                label={bid.payroll?.code}
              />
            </td>
            <td>
              <Text size="xs" color="dimmed">
                {formatPayrollBidHeldDate(bid)}
              </Text>
            </td>
            <td style={{ textAlign: "right" }}>
              <Text
                size="xs"
                weight={600}
                color={bid.commissionAmount >= 0 ? "teal" : "red"}
              >
                {formatter.currency(bid.commissionAmount)}
              </Text>
            </td>
            {!isConfirmed && (
              <td style={{ textAlign: "center" }}>
                <Group spacing={4} position="center" noWrap>
                  {bid.status === "HOLD" && (
                    <>
                      <Tooltip label="Release to this payroll">
                        <ActionIcon
                          size="sm"
                          color="green"
                          variant="filled"
                          onClick={() => handleRelease([bid._id])}
                          disabled={loading}
                        >
                          <IconPlayerPlay size={12} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Cancel bid">
                        <ActionIcon
                          size="sm"
                          color="red"
                          variant="filled"
                          onClick={() => handleCancel([bid._id])}
                          disabled={loading}
                        >
                          <IconX size={12} />
                        </ActionIcon>
                      </Tooltip>
                    </>
                  )}
                  {bid.status === "CANCEL" && (
                    <Tooltip label="Revert to Hold">
                      <ActionIcon
                        size="sm"
                        color="orange"
                        variant="filled"
                        onClick={() => handleRevert([bid._id])}
                        disabled={loading}
                      >
                        <IconArrowBack size={12} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const renderWalletHeader = (item) => {
    // Check if this is a group wallet - item.userGroup is populated from server aggregation
    const isGroupWallet = !!item.userGroup;

    // For group wallets, use userGroup name/code; for users, use walletName
    const groupName = isGroupWallet
      ? item.userGroup?.name || item.walletName || "—"
      : null;

    const groupCode = item.userGroup?.code || null;

    return (
      <Grid justify="space-between" align="center">
        <Grid.Col span={3}>
          {isGroupWallet ? (
            <Group noWrap>
              <Avatar radius="xl" color="violet">
                {groupName?.charAt(0)?.toUpperCase() || "G"}
              </Avatar>
              <Group spacing="xs" noWrap>
                {groupCode && (
                  <Badge
                    size="xs"
                    p={0}
                    radius={0}
                    mr={0}
                    w={50}
                    color="violet"
                  >
                    {groupCode}
                  </Badge>
                )}
                <Text lineClamp={1}>{groupName}</Text>
              </Group>
            </Group>
          ) : item.user ? (
            <div>{cellRender.User(item.user)}</div>
          ) : (
            <div>
              <Badge mb="xs">{item.walletName || "—"}</Badge>
            </div>
          )}
        </Grid.Col>
        <Grid.Col span={2}>
          <Stack align="flex-start" spacing="xs">
            <Group spacing="xs">
              <Title order={4}>{item.bidCount}</Title>
              {item.holdCount > 0 && (
                <Badge size="xs" color="orange" variant="filled">
                  {item.holdCount} hold
                </Badge>
              )}
              {item.cancelCount > 0 && (
                <Badge size="xs" color="red" variant="filled">
                  {item.cancelCount} cancel
                </Badge>
              )}
            </Group>
            <Text size="xs" color="dimmed">
              Bids
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={3}>
          <Stack align="flex-start" spacing="xs">
            <Title order={4} color={item.holdAmount >= 0 ? "orange" : "red"}>
              {formatter.currency(item.holdAmount || 0)}
            </Title>
            <Text size="xs" color="dimmed">
              Hold Amount
            </Text>
          </Stack>
        </Grid.Col>
        {!isConfirmed && (
          <Grid.Col span={3}>
            <div onClick={(e) => e.stopPropagation()}>
              <Group spacing="xs" noWrap>
                <Tooltip label="Release all to this payroll">
                  <Button
                    size="xs"
                    color="green"
                    variant="light"
                    compact
                    disabled={loading || item.holdCount === 0}
                    onClick={() => handleReleaseWallet(item.walletId)}
                  >
                    Release All
                  </Button>
                </Tooltip>
                <Tooltip label="Cancel all hold bids">
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    compact
                    disabled={loading || item.holdCount === 0}
                    onClick={() => handleCancelWallet(item.walletId)}
                  >
                    Cancel All
                  </Button>
                </Tooltip>
              </Group>
            </div>
          </Grid.Col>
        )}
      </Grid>
    );
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={2} />

      <Paper p="md" withBorder mb="lg">
        <Group position="apart">
          <Box>
            <Title order={5}>Holding Bids</Title>
            <Text size="xs" color="dimmed" mt="xs">
              Bids on hold from previous completed payrolls. Release them to
              include in this payroll, or cancel to exclude from future
              payrolls.
            </Text>
          </Box>
          <Group spacing="xs">
            <Tooltip label="Download the holding bids">
              <Button
                size="xs"
                color="teal"
                variant="light"
                leftIcon={<IconDownload size={14} />}
                onClick={handleDownloadHoldingReport}
                loading={downloadLoading}
                disabled={!canExportHoldingReport || loading}
              >
                Download
              </Button>
            </Tooltip>
            <Button
              size="xs"
              color="orange"
              variant="light"
              onClick={reload}
              loading={loading}
            >
              Refresh
            </Button>
          </Group>
        </Group>
        {isConfirmed && (
          <Text size="xs" color="orange" mt="xs">
            Payroll is completed. No more edits allowed.
          </Text>
        )}
      </Paper>

      {totalBids > 0 && (
        <Grid mb="lg" gutter="md">
          <Grid.Col span={3}>
            <Paper p="md" withBorder>
              <Text size="xs" color="dimmed" mb={4}>
                Total Holding Bids
              </Text>
              <Group position="apart" align="flex-end">
                <Text size="xl" weight={700}>
                  {totalBids}
                </Text>
                <Text size="md" weight={600} color="orange">
                  {formatter.currency(totalHoldAmount + totalCancelAmount)}
                </Text>
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={3}>
            <Paper
              p="md"
              withBorder
              style={{ borderLeft: "3px solid #fd7e14" }}
            >
              <Group position="apart" align="center" mb={4}>
                <Text size="xs" color="dimmed">
                  On Hold
                </Text>
                <Badge size="sm" color="orange" variant="filled">
                  HOLD
                </Badge>
              </Group>
              <Group position="apart" align="flex-end">
                <Text size="xl" weight={700} color="orange">
                  {holdCount}
                </Text>
                <Text size="md" weight={600} color="orange">
                  {formatter.currency(totalHoldAmount)}
                </Text>
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={3}>
            <Paper
              p="md"
              withBorder
              style={{ borderLeft: "3px solid #fa5252" }}
            >
              <Group position="apart" align="center" mb={4}>
                <Text size="xs" color="dimmed">
                  Cancelled
                </Text>
                <Badge size="sm" color="red" variant="filled">
                  CANCEL
                </Badge>
              </Group>
              <Group position="apart" align="flex-end">
                <Text size="xl" weight={700} color="red">
                  {cancelCount}
                </Text>
                <Text size="md" weight={600} color="red">
                  {formatter.currency(totalCancelAmount)}
                </Text>
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={3}>
            <Paper
              p="md"
              withBorder
              style={{ borderLeft: "3px solid #40c057" }}
            >
              <Text size="xs" color="dimmed" mb={4}>
                Wallets
              </Text>
              <Text size="xl" weight={700}>
                {summary.length}
              </Text>
            </Paper>
          </Grid.Col>
        </Grid>
      )}

      {(totalBids > 0 || releasedCount > 0 || releasedFromCount > 0) && (
        <Tabs value={innerTab} onTabChange={handleInnerTabChange} keepMounted={false}>
          <Tabs.List mb="md">
            <Tabs.Tab value="byWallets" icon={<IconWallet size={14} />}>
              By Wallets ({summary.length})
            </Tabs.Tab>
            <Tabs.Tab value="byOrders" icon={<IconReceipt size={14} />}>
              By Orders ({orderGroups.length})
            </Tabs.Tab>
            <Tabs.Tab value="allBids" icon={<IconList size={14} />}>
              All Bids ({totalBids})
            </Tabs.Tab>
            <Tabs.Tab
              value="releasedToPayroll"
              icon={<IconCircleCheck size={14} color="#40c057" />}
            >
              <Text component="span" color="green" inherit>
                Released to Payroll ({releasedCount})
              </Text>
            </Tabs.Tab>
            <Tabs.Tab
              value="releasedFromPayroll"
              icon={<IconTransferOut size={14} color="#228be6" />}
            >
              <Text component="span" color="blue" inherit>
                Released from this Payroll ({releasedFromCount})
              </Text>
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="byWallets">
            <Accordion
              variant="separated"
              value={walletAccordionValue}
              onChange={handleWalletAccordionChange}
            >
              {summary.map((item, idx) => {
                const walletKey = item.walletId
                  ? String(item.walletId)
                  : `wallet-${idx}`;
                const walletBids = walletBidsMap[item.walletId] || [];
                return (
                  <Accordion.Item key={walletKey} value={walletKey}>
                    <Accordion.Control>
                      {renderWalletHeader(item)}
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Box pos="relative" style={{ minHeight: 60 }}>
                        <LoadingOverlay
                          visible={loadingWalletId === item.walletId}
                          overlayBlur={1}
                          loaderProps={{ size: "sm" }}
                        />
                        {walletBids.length > 0
                          ? renderBidTable(walletBids, false)
                          : !loadingWalletId && (
                              <Text size="sm" color="dimmed" p="md">
                                No bids loaded yet.
                              </Text>
                            )}
                      </Box>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Tabs.Panel>

          <Tabs.Panel value="allBids">
            <Box pos="relative" style={{ minHeight: 120 }}>
              <LoadingOverlay
                visible={loadingAllBids}
                overlayBlur={1}
                loaderProps={{ size: "sm" }}
              />
              {!isConfirmed && selectedBids.size > 0 && (
                <Paper p="xs" withBorder mb="md">
                  <Group spacing="md">
                    <Text size="xs" weight={500}>
                      {selectedBids.size} selected
                    </Text>
                    <Button
                      size="xs"
                      color="green"
                      variant="light"
                      compact
                      onClick={handleBulkRelease}
                      disabled={loading || loadingAllBids}
                      leftIcon={<IconPlayerPlay size={12} />}
                    >
                      Release Selected
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      compact
                      onClick={handleBulkCancel}
                      disabled={loading || loadingAllBids}
                      leftIcon={<IconX size={12} />}
                    >
                      Cancel Selected
                    </Button>
                    <Button
                      size="xs"
                      color="orange"
                      variant="light"
                      compact
                      onClick={handleBulkRevert}
                      disabled={loading || loadingAllBids}
                      leftIcon={<IconArrowBack size={12} />}
                    >
                      Revert Selected
                    </Button>
                  </Group>
                </Paper>
              )}
              {renderBidTable(allBids, true)}
              {allBidsTotal > allBidsLimit && (
                <Group position="apart" mt="md">
                  <Text size="xs" color="dimmed">
                    Page {allBidsPage} of{" "}
                    {Math.ceil(allBidsTotal / allBidsLimit) || 1} ({allBidsTotal}{" "}
                    bids)
                  </Text>
                  <Group spacing="xs">
                    <Button
                      size="xs"
                      variant="light"
                      disabled={allBidsPage <= 1 || loadingAllBids}
                      onClick={() => loadAllBids(allBidsPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      disabled={
                        allBidsPage >=
                          Math.ceil(allBidsTotal / allBidsLimit) ||
                        loadingAllBids
                      }
                      onClick={() => loadAllBids(allBidsPage + 1)}
                    >
                      Next
                    </Button>
                  </Group>
                </Group>
              )}
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="byOrders">
            {orderGroups.length > 0 ? (
              <>
                <HoldingByOrderFilterBar
                  orderGroups={orderGroups}
                  tableFilterQuery={orderTableFilterQuery}
                  onFilterChange={onOrderTableFilterChange}
                />
                <Group position="apart" mb="md">
                  <Text size="sm" color="dimmed">
                    Showing {filteredOrderGroups.length} of {orderGroups.length}{" "}
                    groups
                  </Text>
                  <Group spacing="xs">
                    <Button
                      size="xs"
                      color="green"
                      leftIcon={<IconPlayerPlay size={14} />}
                      onClick={() => handleBulkOrderClick("release")}
                      disabled={
                        isConfirmed ||
                        loading ||
                        holdingBulkCounts.release === 0
                      }
                    >
                      Release All ({holdingBulkCounts.release})
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      leftIcon={<IconX size={14} />}
                      onClick={() => handleBulkOrderClick("cancel")}
                      disabled={
                        isConfirmed ||
                        loading ||
                        holdingBulkCounts.cancel === 0
                      }
                    >
                      Cancel All ({holdingBulkCounts.cancel})
                    </Button>
                    <Button
                      size="xs"
                      color="orange"
                      leftIcon={<IconArrowBack size={14} />}
                      onClick={() => handleBulkOrderClick("revert")}
                      disabled={
                        isConfirmed ||
                        loading ||
                        holdingBulkCounts.revert === 0
                      }
                    >
                      To Hold All ({holdingBulkCounts.revert})
                    </Button>
                  </Group>
                </Group>
                {filteredOrderGroups.length === 0 ? (
                  <Paper p="xl" withBorder>
                    <Stack align="center" spacing="xs" py="xl">
                      <Text size="lg" weight={500} color="dimmed">
                        No orders match the current filters
                      </Text>
                      <Text size="sm" color="dimmed">
                        Adjust or clear filters to see order groups.
                      </Text>
                    </Stack>
                  </Paper>
                ) : (
                  <Accordion
                    variant="separated"
                    value={orderAccordionValue}
                    onChange={handleOrderAccordionChange}
                  >
                    {filteredOrderGroups.map((order, idx) => {
                      const orderKey = String(
                        order._id || order.key || `order-${idx}`,
                      );
                      return (
                        <Accordion.Item key={orderKey} value={orderKey}>
                          <Accordion.Control>
                            <HoldingByOrderRowHeader
                              order={order}
                              formatter={formatter}
                              cellRender={cellRender}
                              rowNumber={idx + 1}
                            />
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Box pos="relative" style={{ minHeight: 60 }}>
                              <LoadingOverlay
                                visible={loadingOrderKey === orderKey}
                                overlayBlur={1}
                                loaderProps={{ size: "sm" }}
                              />
                              {orderBidsMap[orderKey] !== undefined ? (
                                orderBidsMap[orderKey].length > 0 ? (
                                  renderBidTable(orderBidsMap[orderKey], false)
                                ) : (
                                  <Text size="sm" color="dimmed" p="md">
                                    No bids found.
                                  </Text>
                                )
                              ) : (
                                <Text size="sm" color="dimmed" p="md">
                                  Loading bids...
                                </Text>
                              )}
                            </Box>
                          </Accordion.Panel>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                )}
              </>
            ) : (
              <Paper p="xl" withBorder>
                <Text color="dimmed" align="center">
                  No orders found. Click "By Orders" tab to load.
                </Text>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="releasedToPayroll">
            <Text size="xs" color="dimmed" mb="md">
              Bids released from previous payrolls into this payroll.
            </Text>
            {hasStuckHoldReleasedBids && !isConfirmed && (
              <Alert color="orange" mb="md" variant="light">
                Some released bids are in HOLD status and cannot be settled. Use
                Hold Back All to return them to the pool.
              </Alert>
            )}
            {releasedBids.length > 0 ? (
              <>
                <Group position="apart" mb="md">
                  <Group spacing="md">
                    <Text size="sm" weight={500}>
                      {releasedCount} bid{releasedCount !== 1 ? "s" : ""}
                    </Text>
                    <Text size="sm" weight={600} color="teal">
                      {formatter.currency(releasedTotalAmount)}
                    </Text>
                  </Group>
                  {!isConfirmed && (
                    <Button
                      size="xs"
                      color="orange"
                      variant="light"
                      leftIcon={<IconArrowBack size={14} />}
                      disabled={loading || holdBackEligibleCount === 0}
                      onClick={openHoldBackAllModal}
                    >
                      Hold Back All
                    </Button>
                  )}
                </Group>
                {renderReleasedBidTable(releasedBids)}
              </>
            ) : (
              <Paper p="xl" withBorder>
                <Text color="dimmed" align="center">
                  No bids released to this payroll yet.
                </Text>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="releasedFromPayroll">
            <ReleasedFromPoolTabContent
              bids={releasedFromBids}
              loading={releasedFromLoading}
              releasedCount={releasedFromCount}
              releasedTotalAmount={releasedFromTotalAmount}
              formatter={formatter}
              mode="from"
            />
          </Tabs.Panel>
        </Tabs>
      )}

      {totalBids === 0 &&
        releasedCount === 0 &&
        releasedFromCount === 0 &&
        !loading && (
        <Paper p="xl" withBorder>
          <Text color="dimmed" align="center">
            No holding bids found from previous payrolls.
          </Text>
        </Paper>
      )}

      <Modal
        opened={holdBackAllModalOpened}
        onClose={closeHoldBackAllModal}
        title={<Title order={4}>Hold Back All</Title>}
        size="md"
        centered
      >
        <Stack spacing="md">
          <Text size="sm">
            Return {holdBackEligibleCount} released bid
            {holdBackEligibleCount !== 1 ? "s" : ""} (
            {formatter.currency(holdBackEligibleAmount)}) to the holding pool on
            their original payrolls?
          </Text>
          <Group position="right" mt="md">
            <Button variant="default" onClick={closeHoldBackAllModal}>
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={handleHoldBackAllConfirm}
              loading={loading}
            >
              Hold Back All
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={bulkOrderModalOpened}
        onClose={closeBulkOrderModal}
        title={
          pendingBulkOrderAction === "release"
            ? `Release All (${holdingBulkCounts.release})?`
            : pendingBulkOrderAction === "cancel"
              ? `Cancel All (${holdingBulkCounts.cancel})?`
              : `To Hold All (${holdingBulkCounts.revert})?`
        }
        centered
      >
        <Text size="sm" mb="lg">
          {pendingBulkOrderAction === "release" &&
            `This will release ${holdingBulkCounts.release} bid(s) from the filtered order groups to this payroll.`}
          {pendingBulkOrderAction === "cancel" &&
            `This will cancel ${holdingBulkCounts.cancel} on-hold bid(s) from the filtered order groups.`}
          {pendingBulkOrderAction === "revert" &&
            `This will revert ${holdingBulkCounts.revert} cancelled bid(s) back to hold.`}
        </Text>
        <Group position="right">
          <Button variant="default" onClick={closeBulkOrderModal}>
            Cancel
          </Button>
          <Button
            color={
              pendingBulkOrderAction === "release"
                ? "green"
                : pendingBulkOrderAction === "cancel"
                  ? "red"
                  : "orange"
            }
            onClick={() => executeBulkOrderAction(pendingBulkOrderAction)}
            loading={loading}
          >
            Confirm
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};

export default HoldingBidsTab;
